import * as React from 'react';

import {colors} from 'shells/colors/colors';
// @ts-expect-error - imported from .js/.jsx file
import {LoadingIcon} from 'shells/icon/svg/loading-icon';

import {Button} from '../../button/button';
import {Divider} from '../../layout/divider';
import {Stack} from '../../layout/stack/stack';
import {SelectMenu} from '../../select-menu/select-menu';
import {Body} from '../../typography/body';
import type {RealAndFakeWidgetType} from '../../widgets/types';

import {CollapsibleChevron} from '../collapsible-chevron';

import type {AddButtonProps} from './card-widget';
import {
    getButtonVariantForWidgetType,
    getIconColorForWidgetType,
    getTitleColorForWidgetType,
} from './helpers';

import styles from './card-widget-header.module.css';

type Props = {
    title: string;
    titleIcon?: React.ComponentType<any>;
    secondaryTitle?: React.ReactNode;
    secondaryTitleClickPropagates?: boolean;
    isCollapsible: boolean;
    isCollapsed: boolean;
    onToggleCollapse: () => void;
    addButtonProps?: AddButtonProps;
    secondaryAddButtonProps?: AddButtonProps;
    hasDivider?: boolean;
    count?: number;
    type?: RealAndFakeWidgetType;
    showSpinner?: boolean;
};

export function CardWidgetHeader(props: Props) {
    const [isAddPopoverOpen, setIsAddPopoverOpen] = React.useState<boolean>(false);
    const [isSecondaryAddPopoverOpen, setIsSecondaryAddPopoverOpen] =
        React.useState<boolean>(false);

    const addButtonRef = React.useRef<HTMLButtonElement | null | undefined>(null);
    const secondaryAddButtonRef = React.useRef<HTMLButtonElement | null | undefined>(null);

    const handleAddButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();

        if (!props.addButtonProps) {
            return;
        }

        if (props.isCollapsed) {
            props.onToggleCollapse();
        }

        if (props.addButtonProps.variant === 'singular') {
            props.addButtonProps.onClick();
        } else if (props.addButtonProps.variant === 'multiple') {
            setIsAddPopoverOpen(true);
        }
    };

    const handleSecondaryAddButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();

        if (!props.secondaryAddButtonProps) {
            return;
        }

        if (props.isCollapsed) {
            props.onToggleCollapse();
        }

        if (props.secondaryAddButtonProps.variant === 'singular') {
            props.secondaryAddButtonProps.onClick();
        } else if (props.secondaryAddButtonProps.variant === 'multiple') {
            props.secondaryAddButtonProps.onClick?.();
            setIsSecondaryAddPopoverOpen(true);
        }
    };

    const iconColor = getIconColorForWidgetType(props.type);
    const titleColor = getTitleColorForWidgetType(props.type);
    const buttonVariant = getButtonVariantForWidgetType(props.type);

    const widgetTitle = (
        <div className='flex gap-8 full-width align-center justify-sb'>
            <div className='flex gap-8 align-center'>
                {props.titleIcon ? (
                    <div className='flex align-center flex-shrink'>
                        <props.titleIcon
                            size={20}
                            wrapWithDiv={false}
                            color={iconColor}
                            fill={iconColor}
                        />
                    </div>
                ) : null}
                <Body color={titleColor} size='small' isBold={true}>
                    {props.title} {props.count != null && `(${props.count})`}
                </Body>
                {props.secondaryTitle && (
                    <div
                        onClick={
                            props.secondaryTitleClickPropagates
                                ? undefined
                                : (e) => e.stopPropagation()
                        }
                    >
                        {props.secondaryTitle}
                    </div>
                )}
                {props.showSpinner && (
                    <LoadingIcon size={16} fill={colors.green} wrapWithDiv={false} />
                )}
            </div>
            <Stack isHorizontal={true} spacing={12} justifyContent='end'>
                {props.secondaryAddButtonProps && (
                    <Button
                        getButtonRef={(ref: HTMLButtonElement | null | undefined) => {
                            secondaryAddButtonRef.current = ref;
                            props.secondaryAddButtonProps?.getButtonRef?.(ref);
                        }}
                        variant={buttonVariant}
                        onClick={handleSecondaryAddButtonClick}
                        noPadding={true}
                        size='small'
                        disabled={props.secondaryAddButtonProps.isDisabled}
                    >
                        {props.secondaryAddButtonProps.label || '+ Add'}
                    </Button>
                )}
                {props.addButtonProps && (
                    <Button
                        getButtonRef={(ref: HTMLButtonElement | null | undefined) => {
                            addButtonRef.current = ref;
                            props.addButtonProps?.getButtonRef?.(ref);
                        }}
                        variant={buttonVariant}
                        onClick={handleAddButtonClick}
                        noPadding={true}
                        size='small'
                        disabled={props.addButtonProps.isDisabled}
                    >
                        {props.addButtonProps.label || '+ Add'}
                    </Button>
                )}
                {props.isCollapsible && <CollapsibleChevron isExpanded={!props.isCollapsed} />}
            </Stack>
        </div>
    );

    const Tag = props.isCollapsible ? 'button' : 'div';

    return (
        <>
            <div className='flex full-width pos-relative'>
                <Tag onClick={props.onToggleCollapse} className={styles.header}>
                    {widgetTitle}
                </Tag>
                {isAddPopoverOpen &&
                    props.addButtonProps &&
                    props.addButtonProps.variant === 'multiple' &&
                    props.addButtonProps.selectMenuOptions && (
                        <SelectMenu
                            anchor={addButtonRef.current}
                            onClose={() => setIsAddPopoverOpen(false)}
                            options={props.addButtonProps.selectMenuOptions}
                            location='bottom-left'
                        />
                    )}
                {isSecondaryAddPopoverOpen &&
                    props.secondaryAddButtonProps &&
                    props.secondaryAddButtonProps.variant === 'multiple' &&
                    props.secondaryAddButtonProps.selectMenuOptions && (
                        <SelectMenu
                            anchor={secondaryAddButtonRef.current}
                            onClose={() => setIsSecondaryAddPopoverOpen(false)}
                            options={props.secondaryAddButtonProps.selectMenuOptions}
                            location='bottom-left'
                        />
                    )}
            </div>
            {props.hasDivider && !props.isCollapsed && (
                <div className='mb-8 px-8'>
                    <Divider variant='line--horizontal' />
                </div>
            )}
        </>
    );
}
